import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import ko from "./ko.json";
import en from "./en.json";

/**
 * i18n foundation — ko default, en fallback.
 *
 * Display languages: **8** — ko · en · zh · vi · th · id · mn · ja.
 * 2026-09-01 사용자 결정으로 화면 표시 언어를 ko/en 2종에서 8종으로 확장했다
 * (그 이전 빌드는 「ko · en only」였고, 그보다 앞선 빌드는 6종 키오스크 세트였다).
 * 8종 = 협약 6개국어(한/영/베/몽/중/일) + 현장 체류 인원이 많은 태국어·인도네시아어.
 *
 * ⚠️ **표시 언어와 음성 방송 언어는 별개의 축이다.** 지금은 두 집합의 원소가
 * 우연히 같지만(`alertPhrases.ts :: ALERT_LANGUAGES`), 여기서 `ALERT_LANGUAGES`
 * 를 그대로 재사용해 두 축을 묶지 않는다. 방송 언어(`broadcastLangs` ·
 * `voiceLang`)는 Settings 의 방송 카드와 `useAlertTTS` 가 독립적으로 관리하며,
 * 한국어 화면을 쓰면서 모국어로 음성 경보를 듣는 운용이 실제로 존재한다.
 *
 * 리소스 번들: `ko.json` / `en.json` 은 정본이라 정적 import 로 고정하고, 나머지
 * 6종은 `import.meta.glob` 로 **있으면 붙고 없으면 빈 객체**가 된다. 번역 리소스는
 * 단계적으로 들어오므로, `zh.json` 이 아직 없어도 빌드가 깨지지 않고 `fallbackLng:
 * "en"` 으로 영어가 뜬다. 파일을 추가하는 순간 코드 수정 없이 자동으로 잡힌다.
 *
 * Detection order: an explicit choice persisted in localStorage wins, then we
 * fall back to the browser/`<html lang>`. The chosen language is written back
 * to localStorage so the next visit (and the offline app shell) stays in the
 * same language. A stored value outside the supported set (a stale pick, or a
 * language we dropped) is pruned on boot so detection resolves cleanly.
 *
 * `react: { useSuspense: false }` keeps `useTranslation()` synchronous — these
 * resources are bundled, so there is nothing to suspend on, and disabling
 * suspense avoids accidental fallbacks during the first paint.
 */

export const SUPPORTED_LANGUAGES = [
  "ko",
  "en",
  "zh",
  "vi",
  "th",
  "id",
  "mn",
  "ja",
] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = "expertgas.lang.v3";

/** BCP-47 tag per display language — for `Intl` / `toLocaleString` formatting. */
const INTL_LOCALE: Record<Language, string> = {
  ko: "ko-KR",
  en: "en-US",
  zh: "zh-CN",
  vi: "vi-VN",
  th: "th-TH",
  id: "id-ID",
  mn: "mn-MN",
  ja: "ja-JP",
};

/**
 * Drop a persisted language we no longer support, so the detector never
 * resolves to a bundle that does not exist. Runs before init.
 *
 * ⚠️ 2026-09-01 이전 버전은 `base !== "ko" && base !== "en"` 로 **지원 언어를
 * 지우는** 코드였다. 8종 확장 뒤 그대로 두면 사용자가 고른 zh/vi/th/id 를 부팅
 * 때마다 삭제해 버린다. 판정 기준은 반드시 `SUPPORTED_LANGUAGES` 하나여야 한다.
 */
function pruneUnsupportedStoredLanguage(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) return;
    const base = stored.split("-")[0];
    if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
      // Unsupported pick — drop it so detection falls through to the browser.
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    }
  } catch {
    /* localStorage unavailable (private mode) — ignore, detector handles it. */
  }
}

pruneUnsupportedStoredLanguage();

/**
 * Optional resource bundles, discovered at build time. A language with no
 * `<lng>.json` yet simply gets `{}` and falls back to en.
 */
const OPTIONAL_BUNDLES = import.meta.glob<Record<string, unknown>>("./*.json", {
  eager: true,
  import: "default",
});

function bundleFor(lng: Language): Record<string, unknown> {
  if (lng === "ko") return ko as Record<string, unknown>;
  if (lng === "en") return en as Record<string, unknown>;
  return OPTIONAL_BUNDLES[`./${lng}.json`] ?? {};
}

/** Display languages that actually ship a (non-empty) translation bundle. */
export const TRANSLATED_LANGUAGES: readonly Language[] = SUPPORTED_LANGUAGES.filter(
  (lng) => Object.keys(bundleFor(lng)).length > 0,
);

const resources = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((lng) => [lng, { translation: bundleFor(lng) }]),
);

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Don't downgrade "ko-KR" → fail; treat the base language as the match.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "htmlTag", "navigator"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

/** Switch language and mirror onto <html lang> + localStorage. */
export function changeLanguage(lng: Language): void {
  void i18n.changeLanguage(lng);
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
}

/** Narrow an arbitrary string (server `locale`, stored value) to a Language. */
export function toLanguage(value: string | null | undefined): Language | null {
  const base = (value ?? "").split("-")[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as Language)
    : null;
}

/** Effective base language right now (one of `SUPPORTED_LANGUAGES`). */
export function currentLanguage(): Language {
  return toLanguage(i18n.resolvedLanguage ?? i18n.language ?? "ko") ?? "ko";
}

/** BCP-47 tag for the current (or given) display language — for `Intl`. */
export function intlLocale(lng: Language = currentLanguage()): string {
  return INTL_LOCALE[lng];
}

// Keep <html lang> in sync whenever the language changes (e.g. via the toggle
// or the detector), so screen readers and the manifest see the right locale.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng.split("-")[0];
  }
});

// ⚠️ 위 리스너는 **초기 로드에는 걸리지 않는다** — `init()` 이 감지한 언어에 대한
//    `languageChanged` 는 이 리스너가 등록되기 전에 이미 발생한다. 그래서 저장된 언어가
//    베트남어여도 `<html lang>` 은 "ko" 로 남았고, 스크린리더·브라우저 번역이 베트남어 화면을
//    한국어로 취급했다(2026-09-01 실측). 등록 직후 한 번 현재값으로 맞춘다.
if (typeof document !== "undefined") {
  document.documentElement.lang = currentLanguage();
}

export default i18n;
